import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import ProjectDashboard from '@/pages/ProjectDashboard';
import ProjectChatPanel from '@/components/ProjectChat';
import { FeatureLocked } from '@/components/FeatureLocked';
import { ArrowLeft, Calendar, IndianRupee, FileText, CheckSquare, Plus, Upload, MessageCircle, BarChart3, Wallet, Link, FolderOpen, Trash2, ExternalLink } from 'lucide-react';
import { API } from '@/App';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/dateFormat';

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [boqDialogOpen, setBoqDialogOpen] = useState(false);
  const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
  const [uploadingBOQ, setUploadingBOQ] = useState(false);
  
  // Document upload state
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState('link');
  const [documentForm, setDocumentForm] = useState({
    name: '',
    url: '',
    doc_type: 'drawing'
  });
  const [documentUploading, setDocumentUploading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [chatOpen, setChatOpen] = useState(searchParams.get('tab') === 'chat');
  const [financialOpen, setFinancialOpen] = useState(searchParams.get('tab') === 'financial');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Open chat panel if URL has ?tab=chat
  useEffect(() => {
    if (searchParams.get('tab') === 'chat') {
      setChatOpen(true);
    }
  }, [searchParams]);

  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    deadline: '',
    status: 'not_started',
    task_cost: '',
  });

  useEffect(() => {
    fetchProjectDetails();
    if (user.role === 'owner' || user.role === 'staff') {
      fetchUsers();
    }
  }, [id]);

  const fetchProjectDetails = async () => {
    try {
      const [projectRes, tasksRes, docsRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/tasks?project_id=${id}`),
        axios.get(`${API}/documents?project_id=${id}`),
      ]);

      setProject(projectRes.data);
      setTasks(tasksRes.data);
      setDocuments(docsRes.data);

      if (user.role !== 'vendor') {
        const paymentsRes = await axios.get(`${API}/payments?project_id=${id}`);
        setPayments(paymentsRes.data);
      }
    } catch (error) {
      toast.error('Failed to load project details');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      const vendorsAndLabour = response.data.filter((u) => u.role === 'vendor' || u.role === 'labour');
      setUsers(vendorsAndLabour);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tasks`, {
        project_id: id,
        ...taskFormData,
        task_cost: taskFormData.task_cost ? parseFloat(taskFormData.task_cost) : null,
        deadline: new Date(taskFormData.deadline).toISOString(),
      });
      toast.success('Task created successfully');
      setTaskDialogOpen(false);
      fetchProjectDetails();
      setTaskFormData({
        title: '',
        description: '',
        assigned_to: '',
        deadline: '',
        status: 'not_started',
        task_cost: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleBOQUpload = async (e) => {
    e.preventDefault();
    const fileInput = document.getElementById('boq-file');
    const file = fileInput?.files[0];

    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploadingBOQ(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/tasks/import-boq?project_id=${id}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      toast.success(
        `Successfully imported ${response.data.tasks_created} tasks!${
          response.data.errors.length > 0 
            ? ` (${response.data.errors.length} rows had errors)` 
            : ''
        }`
      );
      
      if (response.data.errors.length > 0) {
        console.log('Import errors:', response.data.errors);
      }

      setBoqDialogOpen(false);
      fetchProjectDetails();
      fileInput.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import BOQ');
    } finally {
      setUploadingBOQ(false);
    }
  };

  // Document handlers
  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!documentForm.name || !documentForm.url) {
      toast.error('Please provide document name and URL');
      return;
    }
    
    setDocumentUploading(true);
    try {
      await axios.post(`${API}/projects/${id}/documents`, {
        name: documentForm.name,
        url: documentForm.url,
        doc_type: documentForm.doc_type,
        source: uploadType
      });
      toast.success('Document added successfully');
      setDocumentDialogOpen(false);
      setDocumentForm({ name: '', url: '', doc_type: 'drawing' });
      setUploadType('link');
      fetchProjectDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add document');
    } finally {
      setDocumentUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API}/projects/${id}/documents/${docId}`);
      toast.success('Document deleted');
      fetchProjectDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setTaskFormData({
      title: task.title,
      description: task.description || '',
      assigned_to: task.assigned_to,
      deadline: new Date(task.deadline).toISOString().split('T')[0],
      status: task.status,
      task_cost: task.task_cost || '',
    });
    setEditTaskDialogOpen(true);
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/tasks/${selectedTask.id}`, {
        ...taskFormData,
        task_cost: taskFormData.task_cost ? parseFloat(taskFormData.task_cost) : null,
        deadline: new Date(taskFormData.deadline).toISOString(),
      });
      toast.success('Task updated successfully');
      setEditTaskDialogOpen(false);
      setSelectedTask(null);
      fetchProjectDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Task deleted successfully');
      fetchProjectDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: 'status-pending',
      in_progress: 'status-in-progress',
      completed: 'status-completed',
      delayed: 'status-delayed',
      not_started: 'status-pending',
    };
    return classes[status] || 'status-pending';
  };

  const getStatusLabel = (status) => {
    return status.replace('_', ' ');
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12" data-testid="loading-state">Loading...</div>
      </Layout>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <Layout>
      <div data-testid="project-detail-page">
        {/* Back Button */}
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Back to Projects
        </button>

        {/* iter-386u-c — Project Overview header block relocated to
            OverviewTab in ProjectAccounts.jsx. A slim heading + Chat
            button remain so the page still has an identifier and the
            chat entry point is preserved. */}
        <div className="flex items-center justify-between mb-6" data-testid="project-header-slim">
          <div>
            <h1 className="font-playfair text-3xl font-bold text-stone-900" data-testid="project-title">
              {project.name}
            </h1>
            <span className={`status-badge ${getStatusClass(project.status)} mt-1 inline-block`} data-testid="project-status">
              {getStatusLabel(project.status)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* iter-386u-p — Owner/Admin/Staff see the full Project Overview.
                Vendors get a scoped RA Bill / Ledger view instead. */}
            {['owner', 'admin', 'staff'].includes(user.role) && (
              <Button
                onClick={() => navigate(`/project-accounts?projectId=${id}&tab=overview`)}
                variant="outline"
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
                data-testid="project-dashboard-btn"
              >
                <BarChart3 size={18} className="mr-2" />
                Project Overview
              </Button>
            )}
            {['owner', 'admin', 'staff'].includes(user.role) && (
              <Button
                onClick={() => setFinancialOpen((v) => !v)}
                variant={financialOpen ? 'default' : 'outline'}
                className={financialOpen ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-emerald-500 text-emerald-700 hover:bg-emerald-50'}
                data-testid="project-financial-overview-btn"
                aria-pressed={financialOpen}
              >
                <Wallet size={18} className="mr-2" />
                {financialOpen ? 'Back to Project' : 'Financial Overview'}
              </Button>
            )}
            {user.role === 'vendor' && (
              <FeatureLocked capability="can_view_ledger" label="Project Ledger is Essential+">
                <Button
                  onClick={() => navigate(`/vendor/project-ledger/${id}`)}
                  variant="outline"
                  className="border-purple-500 text-purple-600 hover:bg-purple-50"
                  data-testid="vendor-project-ledger-btn"
                >
                  <BarChart3 size={18} className="mr-2" />
                  Project Ledger
                </Button>
              </FeatureLocked>
            )}
            <Button
              onClick={() => setChatOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="project-chat-btn"
            >
              <MessageCircle size={18} className="mr-2" />
              Project Chat
            </Button>
          </div>
        </div>

        {financialOpen && ['owner', 'admin', 'staff'].includes(user.role) && (
          <div data-testid="project-financial-overview-panel">
            <ProjectDashboard embedded />
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="tasks" className={financialOpen && ['owner', 'admin', 'staff'].includes(user.role) ? 'hidden' : 'w-full'}>
          <TabsList className="mb-8" data-testid="project-tabs">
            <TabsTrigger value="tasks" data-testid="tasks-tab">Tasks ({tasks.length})</TabsTrigger>
            <TabsTrigger value="documents" data-testid="documents-tab">Documents ({documents.length})</TabsTrigger>
            {user.role !== 'vendor' && (
              <TabsTrigger value="payments" data-testid="payments-tab">Payments ({payments.length})</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="tasks">
            {/* Task Actions */}
            {(user.role === 'owner' || user.role === 'staff') && (
              <div className="flex gap-3 mb-6">
                <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary flex items-center gap-2" data-testid="create-task-button">
                      <Plus size={18} />
                      New Task
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl" data-testid="create-task-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-playfair text-2xl">Create Task for {project.name}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateTask} className="space-y-4">
                      <div>
                        <Label>Task Title</Label>
                        <Input
                          required
                          value={taskFormData.title}
                          onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                          data-testid="task-title-input"
                        />
                      </div>

                      <div>
                        <Label>Description</Label>
                        <Textarea
                          rows={3}
                          value={taskFormData.description}
                          onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                          data-testid="task-description-input"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Assign To</Label>
                          <Select
                            value={taskFormData.assigned_to}
                            onValueChange={(value) => setTaskFormData({ ...taskFormData, assigned_to: value })}
                          >
                            <SelectTrigger data-testid="assign-to-select">
                              <SelectValue placeholder="Select vendor" />
                            </SelectTrigger>
                            <SelectContent>
                              {users.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Deadline</Label>
                          <Input
                            type="date"
                            required
                            value={taskFormData.deadline}
                            onChange={(e) => setTaskFormData({ ...taskFormData, deadline: e.target.value })}
                            data-testid="deadline-input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Task Cost (₹)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={taskFormData.task_cost}
                            onChange={(e) => setTaskFormData({ ...taskFormData, task_cost: e.target.value })}
                            placeholder="Enter cost"
                            data-testid="task-cost-input"
                          />
                        </div>
                        <div>
                          <Label>Status</Label>
                          <Select
                            value={taskFormData.status}
                            onValueChange={(value) => setTaskFormData({ ...taskFormData, status: value })}
                          >
                            <SelectTrigger data-testid="task-status-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="not_started">Not Started</SelectItem>
                              <SelectItem value="pending_approval">Pending Approval</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="delayed">Delayed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="flex gap-3 justify-end">
                        <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="btn-primary" data-testid="submit-task-button">
                          Create Task
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog open={boqDialogOpen} onOpenChange={setBoqDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="flex items-center gap-2" data-testid="import-boq-button">
                      <Upload size={18} />
                      Import BOQ
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="import-boq-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-playfair text-2xl">Import Bill of Quantity (BOQ)</DialogTitle>
                      <p className="text-sm text-stone-600 mt-2">
                        Upload an Excel or CSV file with tasks. Required columns: <strong>title</strong> (or task/item), 
                        <strong> assigned_to_email</strong> (vendor email). Optional: description, deadline, status.
                      </p>
                    </DialogHeader>
                    <form onSubmit={handleBOQUpload} className="space-y-4">
                      <div>
                        <Label>Select File (.xlsx, .xls, .csv)</Label>
                        <Input
                          id="boq-file"
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          required
                          className="mt-2"
                          data-testid="boq-file-input"
                        />
                      </div>

                      <div className="bg-stone-50 p-4 rounded text-sm">
                        <p className="font-medium mb-2">Example Format:</p>
                        <div className="font-mono text-xs bg-white p-2 rounded">
                          <div>title, description, assigned_to_email, deadline, status</div>
                          <div>Install tiles, Kitchen flooring, vendor@example.com, 2025-01-15, not_started</div>
                        </div>
                      </div>

                      <div className="flex gap-3 justify-end">
                        <Button type="button" variant="outline" onClick={() => setBoqDialogOpen(false)} disabled={uploadingBOQ}>
                          Cancel
                        </Button>
                        <Button type="submit" className="btn-primary" disabled={uploadingBOQ} data-testid="submit-boq-button">
                          {uploadingBOQ ? 'Importing...' : 'Import Tasks'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            {tasks.length === 0 ? (
              <div className="text-center py-16 bg-white border border-stone-200" data-testid="no-tasks">
                <p className="text-stone-500">No tasks yet for this project.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="tasks-list">
                {tasks.map((task) => (
                  <div key={task.id} className="bg-white border border-stone-200 p-6" data-testid={`task-${task.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-stone-900" data-testid="task-title">{task.title}</h3>
                        {task.task_cost && user.role !== 'client' && (
                          <p className="text-stone-800 font-semibold mt-1" data-testid="task-cost">
                            Vendor Cost: ₹{task.task_cost.toLocaleString()}
                          </p>
                        )}
                        {task.client_visible_cost && user.role === 'client' && (
                          <p className="text-accent font-semibold mt-1" data-testid="client-visible-cost">
                            Cost: ₹{task.client_visible_cost.toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 items-center">
                        <span className={`status-badge ${getStatusClass(task.status)}`} data-testid="task-status">
                          {getStatusLabel(task.status)}
                        </span>
                        
                        {/* Edit Button - Only for Owner/Staff on non-approved tasks */}
                        {!task.approved && (user.role === 'owner' || user.role === 'staff') && (
                          <button
                            onClick={() => handleEditTask(task)}
                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                            data-testid="edit-task-button"
                          >
                            Edit
                          </button>
                        )}
                        
                        {/* Approve Button */}
                        {task.task_cost && !task.approved && (user.role === 'owner' || user.role === 'staff') && (
                          <button
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/tasks/${task.id}/approve`);
                                toast.success('Task approved! Cost added to BOQ.');
                                fetchProjectDetails();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to approve task');
                              }
                            }}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                            data-testid="approve-task-button"
                          >
                            Approve
                          </button>
                        )}
                        
                        {/* Delete Button */}
                        {!task.approved && (user.role === 'owner' || user.role === 'staff') && (
                          <button
                            onClick={() => handleDeleteTask(task.id)}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                            data-testid="delete-task-button"
                          >
                            Delete
                          </button>
                        )}
                        
                        {task.approved && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded" data-testid="approved-badge">
                            ✓ Approved
                          </span>
                        )}
                      </div>
                    </div>
                    {task.description && (
                      <p className="text-stone-600 mb-3" data-testid="task-description">{task.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-stone-500">
                      <span data-testid="task-deadline">Deadline: {formatDate(new Date(task.deadline))}</span>
                      {task.work_status && <span data-testid="task-work-status">Work: {task.work_status}</span>}
                      {task.material_status && <span data-testid="task-material-status">Material: {task.material_status}</span>}
                      {task.transport_status && <span data-testid="task-transport-status">Transport: {task.transport_status}</span>}
                    </div>
                  </div>
                ))}
                
                {/* Edit Task Dialog */}
                <Dialog open={editTaskDialogOpen} onOpenChange={setEditTaskDialogOpen}>
                  <DialogContent className="max-w-2xl" data-testid="edit-task-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-playfair text-2xl">Edit Task</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUpdateTask} className="space-y-4">
                      <div>
                        <Label>Task Title</Label>
                        <Input
                          required
                          value={taskFormData.title}
                          onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                          data-testid="task-title-input"
                        />
                      </div>

                      <div>
                        <Label>Description</Label>
                        <Textarea
                          rows={3}
                          value={taskFormData.description}
                          onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                          data-testid="task-description-input"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Assign To</Label>
                          <Select
                            value={taskFormData.assigned_to}
                            onValueChange={(value) => setTaskFormData({ ...taskFormData, assigned_to: value })}
                          >
                            <SelectTrigger data-testid="assign-to-select">
                              <SelectValue placeholder="Select vendor" />
                            </SelectTrigger>
                            <SelectContent>
                              {users.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Deadline</Label>
                          <Input
                            type="date"
                            required
                            value={taskFormData.deadline}
                            onChange={(e) => setTaskFormData({ ...taskFormData, deadline: e.target.value })}
                            data-testid="deadline-input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Task Cost (₹)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={taskFormData.task_cost}
                            onChange={(e) => setTaskFormData({ ...taskFormData, task_cost: e.target.value })}
                            placeholder="Enter cost"
                            data-testid="task-cost-input"
                          />
                        </div>
                        <div>
                          <Label>Status</Label>
                          <Select
                            value={taskFormData.status}
                            onValueChange={(value) => setTaskFormData({ ...taskFormData, status: value })}
                          >
                            <SelectTrigger data-testid="task-status-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="not_started">Not Started</SelectItem>
                              <SelectItem value="pending_approval">Pending Approval</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="delayed">Delayed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="flex gap-3 justify-end">
                        <Button type="button" variant="outline" onClick={() => setEditTaskDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="btn-primary" data-testid="update-task-button">
                          Update Task
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                
                {/* Generate BOQ Button */}
                {tasks.some(t => t.approved) && (user.role === 'owner' || user.role === 'staff') && (
                  <div className="mt-6 p-6 bg-accent/5 border border-accent/20 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-bold text-stone-900 mb-1">Ready to Generate BOQ</h4>
                        <p className="text-sm text-stone-600">
                          {tasks.filter(t => t.approved).length} approved task(s) with total cost of ₹
                          {tasks.filter(t => t.approved).reduce((sum, t) => sum + (t.task_cost || 0), 0).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            const response = await axios.post(`${API}/boq-worksheets?project_id=${id}`);
                            toast.success(`BOQ Worksheet ${response.data.worksheet_number} created!`);
                            navigate(`/boq-worksheets/${response.data.id}`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to generate BOQ');
                          }
                        }}
                        className="apple-button"
                        data-testid="generate-boq-button"
                      >
                        Generate BOQ Worksheet
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="documents">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-stone-900">Project Documents</h3>
                <p className="text-sm text-stone-500">Upload drawings, site photos, and link cloud documents</p>
              </div>
              {(user.role === 'owner' || user.role === 'staff' || user.role === 'admin') && (
                <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="flex items-center gap-2" data-testid="add-document-button">
                      <Plus size={16} />
                      Add Document
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Add Project Document</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddDocument} className="space-y-4">
                      <div className="grid grid-cols-4 gap-2">
                        <Button
                          type="button"
                          variant={uploadType === 'link' ? 'default' : 'outline'}
                          onClick={() => setUploadType('link')}
                          className="flex flex-col items-center p-3 h-auto"
                        >
                          <Link size={20} />
                          <span className="text-xs mt-1">URL Link</span>
                        </Button>
                        <Button
                          type="button"
                          variant={uploadType === 'gdrive' ? 'default' : 'outline'}
                          onClick={() => setUploadType('gdrive')}
                          className="flex flex-col items-center p-3 h-auto"
                        >
                          <FolderOpen size={20} />
                          <span className="text-xs mt-1">G-Drive</span>
                        </Button>
                        <Button
                          type="button"
                          variant={uploadType === 'onedrive' ? 'default' : 'outline'}
                          onClick={() => setUploadType('onedrive')}
                          className="flex flex-col items-center p-3 h-auto"
                        >
                          <FolderOpen size={20} />
                          <span className="text-xs mt-1">OneDrive</span>
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          disabled
                          className="flex flex-col items-center p-3 h-auto opacity-50"
                        >
                          <Upload size={20} />
                          <span className="text-xs mt-1">Upload</span>
                        </Button>
                      </div>
                      
                      {uploadType === 'gdrive' && (
                        <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                          <p className="font-medium mb-1">How to get Google Drive link:</p>
                          <ol className="list-decimal list-inside space-y-1 text-xs">
                            <li>Open your file in Google Drive</li>
                            <li>Click "Share" → "Anyone with the link"</li>
                            <li>Copy the link and paste below</li>
                          </ol>
                        </div>
                      )}
                      
                      {uploadType === 'onedrive' && (
                        <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                          <p className="font-medium mb-1">How to get OneDrive link:</p>
                          <ol className="list-decimal list-inside space-y-1 text-xs">
                            <li>Open your file in OneDrive</li>
                            <li>Click "Share" → "Copy link"</li>
                            <li>Paste the link below</li>
                          </ol>
                        </div>
                      )}

                      <div>
                        <Label>Document Name *</Label>
                        <Input
                          value={documentForm.name}
                          onChange={(e) => setDocumentForm({ ...documentForm, name: e.target.value })}
                          placeholder="e.g., Floor Plan - Ground Floor"
                        />
                      </div>
                      
                      <div>
                        <Label>{uploadType === 'gdrive' ? 'Google Drive Link *' : uploadType === 'onedrive' ? 'OneDrive Link *' : 'Document URL *'}</Label>
                        <Input
                          value={documentForm.url}
                          onChange={(e) => setDocumentForm({ ...documentForm, url: e.target.value })}
                          placeholder="https://..."
                        />
                      </div>
                      
                      <div>
                        <Label>Document Type</Label>
                        <Select
                          value={documentForm.doc_type}
                          onValueChange={(value) => setDocumentForm({ ...documentForm, doc_type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="drawing">Drawing / Plan</SelectItem>
                            <SelectItem value="image">Site Photo</SelectItem>
                            <SelectItem value="3d_render">3D Render</SelectItem>
                            <SelectItem value="contract">Contract</SelectItem>
                            <SelectItem value="invoice">Invoice</SelectItem>
                            <SelectItem value="specification">Specification</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex gap-3 justify-end">
                        <Button type="button" variant="outline" onClick={() => setDocumentDialogOpen(false)}>Cancel</Button>
                        <Button type="submit" disabled={documentUploading}>
                          {documentUploading ? 'Adding...' : 'Add Document'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {documents.length === 0 ? (
              <div className="text-center py-16 bg-white border border-stone-200" data-testid="no-documents">
                <FolderOpen size={48} className="mx-auto text-stone-300 mb-4" />
                <p className="text-stone-500">No documents uploaded yet.</p>
                <p className="text-sm text-stone-400 mt-2">Add drawings, site photos, and other project documents</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="documents-grid">
                {documents.map((doc) => (
                  <div key={doc.id} className="bg-white border border-stone-200 p-6 hover:shadow-md transition-shadow" data-testid={`document-${doc.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <FileText size={32} className="text-stone-800" />
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {doc.source === 'gdrive' ? 'Google Drive' : doc.source === 'onedrive' ? 'OneDrive' : 'Link'}
                        </Badge>
                        {(user.role === 'owner' || user.role === 'admin') && (
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-1 text-red-400 hover:text-red-600 rounded"
                            data-testid={`delete-doc-${doc.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                    <h3 className="font-bold text-stone-900 mb-2" data-testid="document-name">{doc.name}</h3>
                    <p className="text-sm text-stone-500 mb-3 capitalize" data-testid="document-type">{doc.doc_type?.replace('_', ' ')}</p>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                      data-testid="document-link"
                    >
                      <ExternalLink size={14} />
                      Open Document
                    </a>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {user.role !== 'vendor' && (
            <TabsContent value="payments">
              {payments.length === 0 ? (
                <div className="text-center py-16 bg-white border border-stone-200" data-testid="no-payments">
                  <p className="text-stone-500">No payments recorded yet.</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="payments-list">
                  {payments.map((payment) => (
                    <div key={payment.id} className="bg-white border border-stone-200 p-6 flex justify-between items-center" data-testid={`payment-${payment.id}`}>
                      <div>
                        <h3 className="font-bold text-lg text-stone-900 mb-1" data-testid="payment-invoice">
                          {payment.invoice_number}
                        </h3>
                        {payment.description && (
                          <p className="text-stone-600 text-sm mb-2" data-testid="payment-description">{payment.description}</p>
                        )}
                        <p className="text-sm text-stone-500" data-testid="payment-due-date">
                          Due: {formatDate(new Date(payment.due_date))}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-stone-900 mb-2" data-testid="payment-amount">
                          ₹{(payment.amount || 0).toLocaleString()}
                        </p>
                        <span
                          className={`status-badge ${
                            payment.status === 'paid' ? 'status-completed' : payment.status === 'overdue' ? 'status-delayed' : 'status-pending'
                          }`}
                          data-testid="payment-status"
                        >
                          {payment.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Project Chat - Side Panel (NO POP-UP) */}
        <ProjectChatPanel 
          projectId={id}
          projectName={project?.name}
          project={project}
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          onEditProject={() => navigate(`/projects`)}
          users={users}
        />
      </div>
    </Layout>
  );
}